import cn from "classnames";
import { FormEvent, useEffect, useRef, useState } from "react";

// components
import { TrashOutlineIcon } from "components/icons";
import {
  RoundedButton,
  Input,
  Button,
} from "components/LibraryComponents/Components";
import DisplayValue from "components/BusinessProfileSections/DisplayValue";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// utils
import { isAnyInputEmpty, formatCurrentPrincipalsPayload, uuidv4, saveErrorToast } from "utils/edit-profile";
import { getLegalStructureName } from "utils/helper";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// styles
import styles from "./OrganizationSection.module.scss";

import { updateExtendedProfileData } from "data/fetch";

export type PrincipalFormData = {
  id: string;
  name: string;
  title: string;
  isNew?: boolean;
};

const MAX_LIMIT = 45;

function anyUnsavedChanges(
  formData: PrincipalFormData[],
  initialData: PrincipalFormData[]
) {
  return (
    formData.some((item, index) => {
      const initialItem: PrincipalFormData = initialData[index];

      return (
        item.id !== initialItem?.id ||
        item.name !== initialItem?.name ||
        item.title !== initialItem?.title
      );
    }) || formData.length !== initialData.length
  );
}

export default function OrganizationSection({
  setUnsavedChanges,
  businessProfileData,
}: SectionsProps) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [formData, setFormData] = useState<PrincipalFormData[]>([]);
  const [initialData, setInitialData] = useState<PrincipalFormData[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingAddAnother, setPendingAddAnother] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const PRINCIPAL_OWNERS = "Principal owners" as const;

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<typeof PRINCIPAL_OWNERS>();

  const addTask = useMeiliTaskToast();

  const isEmpty = formData.length === 0;
  const showAddMore = formData.length < MAX_LIMIT;
  const isAnyEmpty = isAnyInputEmpty(formData);

  // Parse principal owners from the semicolon-space-separated string
  const parsePrincipals = (
    value: string | null | undefined
  ): PrincipalFormData[] =>
    value
      ? value.split("; ").map((pair) => {
        const [name, title] = pair.split(" - ").map((part) => part.trim());
        return {
          id: uuidv4(),
          name: name || "",
          title: title || "",
        };
      })
      : [];

  useEffect(() => {
    const initialPrincipals = parsePrincipals(
      extendedProfile?.current_principals
    );

    setFormData(initialPrincipals);
    setInitialData([...initialPrincipals]);
  }, [extendedProfile]);

  const unsavedChanges = anyUnsavedChanges(formData, initialData);

  useEffect(() => {
    setUnsavedChanges(unsavedChanges);
    return () => {
      setUnsavedChanges(false);
    };
  }, [formData, setUnsavedChanges]);

  // Add another principal owner only after saving
  useEffect(() => {
    if (pendingAddAnother && !isSaving) {
      handleCreate();
      setPendingAddAnother(false);
    }
  }, [pendingAddAnother, isSaving]);

  const handleCreate = () => {

    if (formData.length < MAX_LIMIT &&
      (formData.length === 0 || formRef.current?.reportValidity())
    ) {

      const newFormData: PrincipalFormData = {
        id: uuidv4(),
        name: "",
        title: "",
        isNew: true, // allow easily removing unsaved items
      }

      setFormData((prev) => [...prev, newFormData]);
    }
  };

  const onChange = (data: PrincipalFormData) => {
    clearConfirmation();
    const updateFormData = formData.map((item) => {
      if (item.id === data.id) {
        return data;
      } else {
        return item;
      }
    });

    setFormData(updateFormData);
  };

  const onDelete = async (id: string) => {
    const itemToDelete = formData.find((item) => item.id === id);

    if (!id || !itemToDelete) return;

    if (itemToDelete.isNew) {
      // If it's a new item, just remove it from the local state
      setFormData((prev) => prev.filter((item) => item.id !== id));
      return;
    }

    const updatedFormData = formatCurrentPrincipalsPayload(
      formData.filter((item) => item.id !== id)
    );

    setDeletingId(id);

    try {
      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          current_principals: updatedFormData
        })
      );

      if (response.ok) {

        setExtendedProfile({
          ...extendedProfile,
          current_principals: updatedFormData,
        });

        setFormData(updatedFormData);

        const responseData = await response.json();
        addTask({
          taskUid: responseData?.taskUid,
          label: PRINCIPAL_OWNERS,
          status: responseData?.status,
        });
        showConfirmation(PRINCIPAL_OWNERS);
      }
    } catch (error) {
      console.error(`Error deleting principal owner:`, error);
      saveErrorToast();
    } finally {
      setDeletingId(null);
    }
  };

  const handleSavePrincipals = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const form = e.target as HTMLFormElement;
    const submitter = (e.nativeEvent as any).submitter;
    const isAddAnother = submitter && submitter.name === "addAnother";

    if (isAddAnother && !unsavedChanges) {
      handleCreate();
      return
    }

    if (!form.reportValidity()) {
      console.log("form is invalid")
      return
    }

    if (!unsavedChanges || isSaving) return;

    setIsSaving(true);
    try {
      const principalOwnersPayload = formatCurrentPrincipalsPayload(formData)

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          current_principals: principalOwnersPayload,
        })
      );

      if (response.ok) {
        setExtendedProfile({
          ...extendedProfile,
          current_principals: principalOwnersPayload
        });

        const newData = formData.map((item) => ({
          ...item,
          isNew: false,
        }));

        setInitialData(newData);
        
        const responseData = await response.json();
        addTask({
          taskUid: responseData?.taskUid,
          label: PRINCIPAL_OWNERS,
          status: responseData?.status,
        });
        showConfirmation(PRINCIPAL_OWNERS);
      }

    } catch (error: any) {
      console.error(`Error saving principal owner:`, error);
      saveErrorToast()
    } finally {
      setIsSaving(false);
      if (isAddAnother) {
        setPendingAddAnother(true);
      }
    }
  };

  return (
    <div className={styles["organization-section-root"]}>
      <form onSubmit={handleSavePrincipals} ref={formRef}>
        <h2 className={styles["heading"]}>Organization & ownership</h2>
        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>Principal owners</h3>
            {/* <p className={styles["desc"]}>
                Add up to 3 principal owners to be listed on your small business’s
                profile page.
              </p> */}
          </div>

          <div className={styles["space-y-4"]}>
            {!isEmpty &&
              formData.map((item, idx) => (
                <PrinicipalForm
                  key={item.id}
                  number={idx + 1}
                  data={item}
                  onChange={onChange}
                  onDelete={onDelete}
                  deletingId={deletingId}
                  isSaving={isSaving}
                />
              ))}

            {isEmpty && (
              <p className={styles["empty-text"]}>
                No principal owners currently listed.
              </p>
            )}

            {showAddMore && (
              <Button
                type="submit"
                name="addAnother"
                className={styles["outline-btn"]}
                disabled={isAnyEmpty || isSaving || deletingId !== null}
                loading={isSaving}
              >
                {
                  isEmpty ? "Add a principal owner" :
                    unsavedChanges ? "Save and add another" : "Add another"}
              </Button>
            )}
          </div>

          {!showAddMore && (
            <p className={styles["prn-limit-text"]}>
              <strong>
                You have entered the maximum number of principal owners.
              </strong>{" "}
              Change your owner list above, or remove existing ones before adding
              more.
            </p>
          )}

          <ResponsiveStack>
            <Button
              type="submit"
              loading={isSaving}
              disabled={
                isEmpty || isAnyEmpty || isSaving || !unsavedChanges || deletingId !== null
              }
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
            {lastSavedSection === PRINCIPAL_OWNERS && <Saved />}
          </ResponsiveStack>
        </div>
      </form>

      {/* Information from SAM.gov */}
      <div className={cn(styles["section-content"], styles["space-y-6"])}>
        <div>
          <h3 className={styles["title"]}>Information from SAM.gov</h3>
          <p className={styles["desc"]}>
            The following items come from this small business’s SAM.gov
            registration, and must be updated there.
            <a
              href="https://www.sam.gov/"
              target="_blank"
              rel="noreferrer"
              className="usa-link usa-link--external"
              aria-label="Visit SAM.gov"
            >
              Log in to SAM.gov
            </a>
          </p>
        </div>

        <div className={styles["space-y-4"]}>
          <div className={styles["group"]}>
            <p className={styles["label"]}>Entity structure</p>
            <DisplayValue
              value={getLegalStructureName(
                businessProfileData?.legal_structure
              )} // Replace "LLC" with actual legal structure from business profile
              className={styles["value"]}
            />
          </div>
        </div>

        <div className={styles["space-y-4"]}>
          <div className={styles["group"]}>
            <p className={styles["label"]}>
              Business type & self-certifications
            </p>
            {businessProfileData?.meili_self_certifications &&
              businessProfileData.meili_self_certifications.length > 0 ? (
              <>
                {
                  <ul className={styles["list"]}>
                    {businessProfileData.meili_self_certifications.map(
                      (cert, i) => (
                        <li key={i}>{cert}</li>
                      )
                    )}
                  </ul>
                }
              </>
            ) : (
              <p className={styles["value"]}>No self-certifications found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

type PrinicipalFormProps = {
  number: number;
  data: PrincipalFormData;
  onChange: (data: PrincipalFormData) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  isSaving: boolean;
};

function PrinicipalForm(props: PrinicipalFormProps) {
  const { number, data, onChange, onDelete, deletingId, isSaving } = props;

  return (
    <div className={cn(styles["principal-form"], styles["space-y-4"])}>
      <div className={styles["header"]}>
        <h4>Principal owner #{number}</h4>

        <RoundedButton
          type="button"
          variant="ghost"
          className={styles["icon-btn"]}
          onClick={() => onDelete(data.id)}
          aria-label="Remove principal owner"
          loading={deletingId === data.id}
          disabled={deletingId !== null || isSaving}
        >
          <TrashOutlineIcon aria-hidden={true} />
          Remove
        </RoundedButton>
      </div>

      <Input
        id="name"
        name="name"
        type="text"
        label="Name"
        placeholder="Type the principal owner’s name here"
        value={data.name}
        suffix={<abbr className={styles["required"]}> *</abbr>}
        onChange={(e) => onChange({ ...data, name: e.target.value })}
        required
      />

      <Input
        id="title"
        name="title"
        type="text"
        label="Title"
        placeholder="Type the principal owner’s title here"
        value={data.title}
        suffix={<abbr className={styles["required"]}> *</abbr>}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
        required
      />
    </div>
  );
}
